import { useEffect } from "react";
import { articles, Article, brandName } from "@/mocks/gossip";

export default function ReaderModal({ article, onClose }: { article: Article | null; onClose: () => void }) {
  useEffect(() => {
    if (!article) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [article, onClose]);

  if (!article) return null;

  const related = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label={article.title}>
      <button
        type="button"
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="关闭"
      ></button>

      <div className="nice-scroll relative flex max-h-[94vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl bg-background-50 sm:rounded-3xl">
        {/* sticky header */}
        <div className="flex items-center justify-between gap-4 border-b border-background-200 bg-background-50/95 px-5 py-4 backdrop-blur md:px-7">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-base">
              <span aria-hidden="true">🍉</span>
            </span>
            <p className="truncate font-heading text-sm font-bold text-foreground-950">
              {brandName} · 深扒专题
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
            aria-label="关闭阅读面板"
          >
            <i className="ri-close-line h-5 w-5"></i>
          </button>
        </div>

        {/* scroll body */}
        <div className="nice-scroll overflow-y-auto px-5 py-6 md:px-8 md:py-8">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
            <span className="rounded-full bg-primary-500 px-2.5 py-1 font-semibold text-background-50">{article.category}</span>
            <span className="whitespace-nowrap">发布于 {article.date}</span>
            <span className="whitespace-nowrap">阅读约 {article.readMin} 分钟</span>
            <span className="flex items-center gap-1 whitespace-nowrap text-accent-700">
              <i className="ri-verified-badge-fill h-3.5 w-3.5"></i>
              信源可溯源
            </span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-black leading-tight tracking-tight text-foreground-950 md:text-3xl">
            {article.title}
          </h1>
          <p className="mt-3 border-l-4 border-primary-400 pl-4 text-base leading-8 text-foreground-700">
            {article.lead}
          </p>

          <figure className="mt-6 overflow-hidden rounded-2xl">
            <img
              src={article.cover}
              alt={`${article.title} 深度解析专题配图`}
              title={`${article.title} · 吃瓜糖心深扒内幕细节与真相还原`}
              className="aspect-[16/9] w-full object-cover"
            />
          </figure>

          <section className="mt-6 rounded-2xl border border-accent-200 bg-accent-100/50 p-5">
            <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-git-merge-line h-4 w-4 text-accent-700"></i>
              本瓜的还原结论
            </h2>
            <p className="mt-2 text-sm leading-7 text-foreground-700">{article.verdict}</p>
          </section>

          <section className="mt-7">
            <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
              <i className="ri-time-line h-5 w-5 text-primary-600"></i>
              事件还原时间线
            </h2>
            <ol className="relative mt-5 space-y-6 border-l-2 border-background-200 pl-6">
              {article.chain.map((c) => (
                <li key={c.time} className="relative">
                  <span className="absolute -left-[1.9rem] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-background-50 bg-primary-500"></span>
                  <p className="text-xs font-bold uppercase tracking-wide text-primary-600">{c.time}</p>
                  <h3 className="mt-1 font-semibold text-foreground-950">{c.title}</h3>
                  <p className="mt-1 text-sm leading-7 text-foreground-700">{c.text}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="mt-7 rounded-2xl border border-secondary-200 bg-secondary-100/60 p-5">
            <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-question-line h-4 w-4 text-secondary-800"></i>
              关键疑点
            </h2>
            <p className="mt-2 text-sm leading-7 text-foreground-700">{article.doubt}</p>
          </section>

          <section className="mt-6 rounded-2xl border border-primary-200 bg-primary-100/40 p-5">
            <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-eye-line h-4 w-4 text-primary-700"></i>
              糖心视角
            </h2>
            <p className="mt-2 text-sm leading-7 text-foreground-700">{article.perspective}</p>
          </section>

          <div className="mt-5 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-700 ring-1 ring-background-200">
                # {t}
              </span>
            ))}
          </div>

          {/* related */}
          <aside className="mt-8 border-t border-background-200 pt-5">
            <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-arrow-right-line h-4 w-4 text-primary-600"></i>
              接着往下吃
            </h3>
            <ul className="mt-3 space-y-2">
              {related.map((r) => (
                <li key={r.id}>
                  <a href="#featured" onClick={onClose} className="flex items-center justify-between gap-3 rounded-xl border border-background-200 bg-background-100/50 px-4 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-200 hover:text-primary-700">
                    {r.title}
                    <i className="ri-arrow-right-up-line h-4 w-4 shrink-0 text-foreground-400"></i>
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
}